<template>
    <div class="ld-container" style="height: 90vh; width: 100%">
        <div class="row" style="height: 100%">
            <div class="list-area col-md-6">
                <div class="row">
                    <header-box :title="`${$t('시스템 관리')} > ${$t('스케줄 관리')}`" />
                </div>
                <!-- 스케줄러 상태 바 -->
                <div class="scheduler-status-bar" v-if="schedulerStatus">
                    <span class="status-label">{{ $t("상태") }}:</span>
                    <span :class="['status-value', isSchedulerRunning ? 'running' : 'stopped']">
                        {{ isSchedulerRunning ? $t("실행 중") : $t("중지") }}
                    </span>
                    <span class="status-info">
                        Cron {{ schedulerStatus.cronScheduler?.jobCount ?? 0 }}건 | Once
                        {{ schedulerStatus.onceScheduler?.jobCount ?? 0 }}건 | {{ $t("플러그인") }}
                        {{ schedulerStatus.pluginJobs?.totalCount ?? 0 }}건
                    </span>
                </div>
                <div style="height: calc(100% - 160px)">
                    <table-list
                        ref="table-list"
                        :title="$t('스케줄 작업')"
                        :columns="columns"
                        :rows="scheduleJobList"
                        :keyField="'name'"
                        :groupField="'group'"
                        :toggleFields="['enabled']"
                        :transCodes="transCodes"
                        :controls="controls"
                        :useMaxHeight="true"
                        @button-click="handleListButtonClick"
                        :mode="'edit'"
                        @row-click="handleItemClick"
                        @toggle-changed="handleItemToggle"
                    />
                </div>
            </div>
            <div class="detail-area col-md-6">
                <ScheduleJobDetail
                    ref="ScheduleJobDetail"
                    :title="$t('스케줄 작업 상세')"
                    :scheduleName="selectedScheduleName"
                    @data-updated="handleDetailUpdated"
                    @closed="handleDetailClosed"
                />
            </div>
        </div>
    </div>
</template>

<script>
import HeaderBox from "@views/component/headerBox/analysis/Header.vue";
import TableList from "@views/component/v2.1/ListDetailView/TableList.vue";
import ScheduleJobDetail from "./ScheduleJobDetail.vue";
import backEndApi from "@api/backEndApi.js";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: {
        HeaderBox,
        TableList,
        ScheduleJobDetail,
    },
    data() {
        return {
            selectedScheduleName: null,
            scheduleJobList: [],
            transCodes: [],
            columns: [
                {
                    label: this.$t("구분"),
                    field: "group",
                    type: "text",
                    sortable: true,
                },
                {
                    label: this.$t("작업명"),
                    field: "name",
                    type: "text",
                    sortable: true,
                },

                {
                    label: this.$t("마지막 실행"),
                    field: "lastRunDisplay",
                    type: "text",
                    width: "140px",
                    sortable: false,
                },
                {
                    label: this.$t("설명"),
                    field: "remark",
                    type: "text",
                    sortable: false,
                    tdClass: "left-align-wrap",
                },
                {
                    label: this.$t("사용"),
                    field: "enabled",
                    type: "text",
                    width: "80px",
                    sortable: false,
                },
            ],
            schedulerStatus: null,
            schedulerActionLoading: false,
            popupMessages,
        };
    },
    computed: {
        isSchedulerRunning() {
            const s = this.schedulerStatus;
            if (!s) return false;
            return !!(s.cronScheduler?.isRunning || s.onceScheduler?.isRunning);
        },
        controls() {
            const list = [{ event: "refresh", label: this.$t("갱신"), class: "bg-white text-black" }];
            if (!this.isSchedulerRunning) {
                list.push({ event: "start", label: this.$t("시작"), class: "btn-success" });
            } else {
                list.push({ event: "stop", label: this.$t("중지"), class: "btn-warning" });
                list.push({ event: "restart", label: this.$t("재시작"), class: "btn-secondary" });
            }
            return list;
        },
    },
    async mounted() {
        await this.getDataList();
        await this.loadSchedulerStatus();
    },
    methods: {
        formatLastRun(val) {
            if (!val) return "";
            if (typeof val === "string" && val.length >= 14) {
                const y = val.slice(0, 4),
                    m = val.slice(4, 6),
                    d = val.slice(6, 8);
                const h = val.slice(8, 10),
                    min = val.slice(10, 12);
                return `${y}-${m}-${d} ${h}:${min}`;
            }
            return val;
        },

        async handleListButtonClick(event) {
            if (this.schedulerActionLoading && ["start", "stop", "restart"].includes(event)) return;
            switch (event) {
                case "refresh":
                    await this.getDataList(event);
                    await this.loadSchedulerStatus();
                    break;
                case "start":
                    await this.handleStartScheduler();
                    break;
                case "stop":
                    await this.handleStopScheduler();
                    break;
                case "restart":
                    await this.handleRestartScheduler();
                    break;
                default:
                    break;
            }
        },

        async handleItemClick(row, idx, toggle, check) {
            if (toggle || check) return;
            const ok = await this.$refs.ScheduleJobDetail.notifyCancelEdit();
            if (ok) {
                this.selectedScheduleName = row.name;
            }
        },

        async getDataList(event) {
            try {
                const result = await backEndApi.scheduleJob.getJobs();
                if (result.status !== 200 || this.$err(result?.data)) {
                    if (result?.data?.message) {
                        this.alertWarning(
                            result.data.message,
                            "",
                            typeof result.data.detail === "string" ? result.data.detail : ""
                        );
                    }
                    return;
                }
                const raw = Array.isArray(result.data) ? result.data : [];
                const sorted = [...raw].sort((a, b) => {
                    const g = (a.group || "").localeCompare(b.group || "");
                    return g !== 0 ? g : (a.name || "").localeCompare(b.name || "");
                });
                this.scheduleJobList = sorted.map((job) => {
                    const isEnabled = !!(job.triggers && job.triggers.some((t) => t.enable));
                    return {
                        ...job,
                        enabled: isEnabled ? "Y" : "N",
                        lastRunDisplay: job.lastRun ? this.formatLastRun(job.lastRun) : "-",
                    };
                });
                if (event) {
                    this.alertNoti(popupMessages.COMMON_REFRESH_POPUP_MESSAGE || "갱신되었습니다.");
                }
            } catch (e) {
                console.error(e);
            }
        },

        async handleDetailUpdated() {
            await this.getDataList();
        },

        handleDetailClosed() {
            this.selectedScheduleName = null;
        },

        async loadSchedulerStatus() {
            try {
                const result = await backEndApi.scheduleJob.getSchedulerStatus();
                if (result.status === 200 && result.data) {
                    this.schedulerStatus = result.data;
                }
            } catch (e) {
                console.error(e);
            }
        },

        async handleStartScheduler() {
            this.schedulerActionLoading = true;
            try {
                const res = await backEndApi.scheduleJob.startScheduler();
                if (res.status === 200 && !this.$err(res?.data)) {
                    this.alertNoti(this.$t("스케줄러가 시작되었습니다."));
                    await this.loadSchedulerStatus();
                    await this.getDataList();
                } else {
                    this.alertWarning(res?.data?.message || this.$t("시작 실패"), "", res?.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("시작 중 오류가 발생했습니다."));
            } finally {
                this.schedulerActionLoading = false;
            }
        },

        async handleStopScheduler() {
            const ok = await this.alertConfirmWarning(this.$t("스케줄러를 중지하면 모든 작업이 멈춥니다. 계속할까요?"));
            if (!ok || !ok.value) return;
            this.schedulerActionLoading = true;
            try {
                const res = await backEndApi.scheduleJob.stopScheduler();
                if (res.status === 200 && !this.$err(res?.data)) {
                    this.alertNoti(this.$t("스케줄러가 중지되었습니다."));
                    await this.loadSchedulerStatus();
                    await this.getDataList();
                } else {
                    this.alertWarning(res?.data?.message || this.$t("중지 실패"), "", res?.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("중지 중 오류가 발생했습니다."));
            } finally {
                this.schedulerActionLoading = false;
            }
        },

        async handleRestartScheduler() {
            const ok = await this.alertConfirmWarning(
                this.$t("스케줄러를 재시작하면 일시적으로 작업이 중단됩니다. 계속할까요?")
            );
            if (!ok || !ok.value) return;
            this.schedulerActionLoading = true;
            try {
                const res = await backEndApi.scheduleJob.restartScheduler();
                if (res.status === 200 && !this.$err(res?.data)) {
                    this.alertNoti(this.$t("스케줄러가 재시작되었습니다."));
                    await this.loadSchedulerStatus();
                    await this.getDataList();
                } else {
                    this.alertWarning(res?.data?.message || this.$t("재시작 실패"), "", res?.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("재시작 중 오류가 발생했습니다."));
            } finally {
                this.schedulerActionLoading = false;
            }
        },

        async handleItemToggle(scheduleName, field, status) {
            if (field !== "enabled") return;
            try {
                const shouldEnable = status === true || status === "Y";
                if (shouldEnable) {
                    const res = await backEndApi.scheduleJob.enableJob(scheduleName);
                    if (res.status === 200 && !this.$err(res?.data)) {
                        this.alertNoti(this.$t("활성화되었습니다."));
                    } else {
                        this.alertWarning(res?.data?.message || this.$t("활성화 실패"), "", res?.data?.detail);
                        await this.getDataList();
                        return;
                    }
                } else {
                    const res = await backEndApi.scheduleJob.disableJob(scheduleName);
                    if (res.status === 200 && !this.$err(res?.data)) {
                        this.alertNoti(this.$t("비활성화되었습니다."));
                    } else {
                        this.alertWarning(res?.data?.message || this.$t("비활성화 실패"), "", res?.data?.detail);
                        await this.getDataList();
                        return;
                    }
                }
                await this.getDataList();
                if (this.selectedScheduleName === scheduleName) {
                    this.$refs.ScheduleJobDetail.loadData && this.$refs.ScheduleJobDetail.loadData();
                }
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("처리 중 오류가 발생했습니다."));
                await this.getDataList();
            }
        },
    },
};
</script>

<style scoped>
.ld-container {
    height: 90vh;
    width: 100%;
    background-color: white;
    border-radius: 10px;
    overflow: hidden;
    padding: 10px 30px 10px 20px;
}

.ld-container .list-area {
    height: 100%;
    overflow: hidden;
    padding: 10px;
}

.ld-container .detail-area {
    height: 100%;
    margin-right: 0;
    padding: 0 10px;
    border-top: 1px solid #f4f4f4;
    border-right: 2px solid #e7e7e7;
    border-bottom: 2px solid #e7e7e7;
    border-left: 1px solid #f4f4f4;
    background-color: #f9f9f9;
    border-radius: 10px;
}

.scheduler-status-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background-color: #f1f1f1;
    border: 1px solid #e7e7e7;
    border-radius: 6px;
    font-size: 12px;
}

.scheduler-status-bar .status-label {
    font-weight: 600;
}

.scheduler-status-bar .status-value.running {
    color: #28a745;
    font-weight: 600;
}

.scheduler-status-bar .status-value.stopped {
    color: #dc3545;
    font-weight: 600;
}

.scheduler-status-bar .status-info {
    color: #666;
}
</style>
